import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, OnInit, Renderer2, ViewChild } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { App } from '@df/core/app';
import { ElementHeightDirective } from '@df/dom/element-height/element-height.directive';
import { AfterContentInitDirective, AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { UiComponent } from '@df/ui/ui.component';
import { EXIT_COMPONENT_EDIT_EVENT } from '@icms/core/integration/icms-core-integration-interfaces';
import { BehaviorSubject } from 'rxjs';
import { PageComponentNotificationBarComponent } from './catalog/page/component/notification-bar/notification-bar.component';
import { OFF_CANVASS_MENU_ZIPPY_CLOSE_EVENT, OffCanvasMenuComponent } from './layout/off-canvas/off-canvas-menu.component';
import { PageFooterComponent } from './layout/page/footer/page-footer.component';
import { PageHeaderComponent } from './layout/page/header/page-header.component';
import { IfNotCheckoutProcessDirective } from './ui/common/if-not-checkout-process.directive';
import { AppBusyStateComponent } from './ui/display/busy-state/app-busy-state.component';
import { RouterAppBusyStateComponent } from './ui/display/busy-state/router-app-busy-state.component';
@Component({
    selector: 'app-root',
    templateUrl: './app-root.html',
    styleUrls: ['./app-root.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        AppBusyStateComponent,
        PageHeaderComponent,
        RouterAppBusyStateComponent,
        ElementHeightDirective,
        RouterOutlet,
        AfterContentInitDirective,
        IfSizeDirective,
        OffCanvasMenuComponent,
        PageFooterComponent,
        IfNotCheckoutProcessDirective,
        CommonModule
    ]
})
export class AppRootComponent extends UiComponent implements OnInit {
    /**
     * If off canvas is visible
     */
    readonly offCanvasState = new BehaviorSubject<boolean>(false);
    hasHeader = false;
    private _pageHeader?: PageHeaderComponent;

    protected isAfterContentInit = false;

    @ViewChild('pageHeader')
    set pageHeader(value: PageHeaderComponent) {
        if (value) {
            const subs = value.hasContent$.subscribe(hasContent => {
                if (!this.hasHeader && hasContent) {
                    this.hasHeader = hasContent;
                    this.detectChanges();
                    this.timeoutService.setTimeout(() => {
                        subs.unsubscribe();
                        this.afterContentInitDirectiveService.unlock(this);
                    });
                }
            });

            const sub2 = this.afterContentInitDirectiveService.ready.subscribe(ready => {
                if (ready) {
                    this.isAfterContentInit = true;
                    this.detectChanges();
                }
                this.timeoutService.setTimeout(() => {
                    sub2.unsubscribe();
                });
            });
        }
        this._pageHeader = value;
    }

    @ViewChild('offCanvas', { read: ElementRef })
    offCanvas?: ElementRef;

    constructor(
        private app: App,
        private afterContentInitDirectiveService: AfterContentInitDirectiveService,
        private renderer: Renderer2,
        private router: Router
    ) {
        super();
    }

    override ngOnInit() {
        this.afterContentInitDirectiveService.lock(this);
        this.offCanvasState.subscribe(state => {
            this.offCanvasState.subscribe(state => {
                this.app.events.broadcast(EXIT_COMPONENT_EDIT_EVENT);
            });
            if (this.offCanvas?.nativeElement) {
                if (state) {
                    this.renderer.removeClass(this.offCanvas?.nativeElement, 'ng-hide');
                } else {
                    this.renderer.addClass(this.offCanvas?.nativeElement, 'ng-hide');
                    this.app.events.broadcast(OFF_CANVASS_MENU_ZIPPY_CLOSE_EVENT);
                }
            }

            this.detectChanges();
        });
        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                // this.applicationRef.tick();
                this.detectChanges();
            }
        });
        super.ngOnInit();
    }

    get pageHeader(): PageHeaderComponent | undefined {
        return this._pageHeader;
    }

    canLoadPageContent = false;
    private _notificationBar?: PageComponentNotificationBarComponent;

    @ViewChild('notificationBar')
    set notificationBar(value: PageComponentNotificationBarComponent) {
        if (!this.canLoadPageContent) {
            if (value) {
                const subs = value.hasContent$.subscribe(hasContent => {
                    if (!this.canLoadPageContent && hasContent) {
                        this.canLoadPageContent = hasContent;
                        this.detectChanges();
                        this.timeoutService.setTimeout(() => {
                            subs.unsubscribe();
                        });
                    }
                });
            } else {
                const url = this.router.url;
                this.canLoadPageContent =
                    !!(url.match(/^\/checkout/) || !!url.match(/^\/sample-checkout/)) &&
                    !url.match(/^\/checkout\/success/) &&
                    !url.match(/^\/sample-checkout\/success/);
            }
        }
        this._notificationBar = value;
    }
    get notificationBar(): PageComponentNotificationBarComponent | undefined {
        return this._notificationBar;
    }
    get hasPageContentClass() {
        return this.isAfterContentInit || !this.app.isPlatformBrowser;
    }
}
